import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, Key, LayoutDashboard, Rocket, Check } from "lucide-react";
import {
  applyPostAuthRedirect,
  setAuthSessionCookies,
} from "@/lib/authCookies";
import { appLoginPath, LANDING_PATHS } from "@/lib/landingPaths";
import { RevealOnScroll, MagneticButton, PartnerSelect } from "../shared";
import { PARTNER_CLIENT_BASE_SELECT_OPTIONS } from "@/constants/partnerProfileOptions";
import {
  PARTNER_FORM_THESIS,
  PARTNER_SCAN,
  UNLOCK_STEPS,
} from "./partnerFormContent";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  name: "",
  email: "",
  website: "",
  clientBase: "",
  niche: "",
};

const STEP_ICONS = { key: Key, dashboard: LayoutDashboard, rocket: Rocket };

const inputClass =
  "w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body";

const normalizeWebsite = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const countFilledFields = (form) => {
  const required = ["name", "email", "clientBase", "niche"];
  const requiredFilled = required.filter((k) => form[k]?.trim()).length;
  const optionalFilled = form.website?.trim() ? 1 : 0;
  return requiredFilled + optionalFilled;
};

const UnlockStep = ({ step, index, isComplete, isActive }) => {
  const Icon = STEP_ICONS[step.icon];

  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="relative flex gap-4"
      data-testid={`unlock-step-${step.step}`}
    >
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-colors ${
            isComplete
              ? "border-[#00D2FF] bg-[#00D2FF]/15"
              : isActive
                ? "border-[#00D2FF]/50 bg-[#00D2FF]/10 shadow-[0_0_24px_rgba(0,210,255,0.15)]"
                : "border-partner bg-[#080A0F]"
          }`}
        >
          {isComplete ? (
            <Check className="w-4 h-4 text-partner-accent" />
          ) : (
            <Icon className={`w-4 h-4 ${isActive ? "text-partner-accent" : "text-partner-muted"}`} />
          )}
        </div>
        {index < UNLOCK_STEPS.length - 1 && (
          <div
            className={`w-px flex-1 min-h-[2rem] mt-2 transition-colors ${
              isComplete ? "bg-[#00D2FF]/50" : "bg-partner"
            }`}
          />
        )}
      </div>

      <div className={`pb-8 ${index === UNLOCK_STEPS.length - 1 ? "pb-0" : ""}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[10px] text-partner-muted">{step.step}</span>
          {isActive && !isComplete && (
            <span className="font-mono text-[9px] text-partner-accent px-1.5 py-0.5 rounded border border-[#00D2FF]/30 bg-[#00D2FF]/5">
              CURRENT
            </span>
          )}
        </div>
        <h3 className={`text-base font-bold mb-1.5 ${isActive || isComplete ? "text-partner" : "text-partner-muted"}`}>
          {step.title}
        </h3>
        <p className="text-sm text-partner-muted font-body leading-relaxed">{step.description}</p>
      </div>
    </motion.div>
  );
};

const PartnerFormSection = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filledCount = useMemo(() => countFilledFields(form), [form]);
  const progress = Math.round((filledCount / 5) * 100);
  const allRequiredFilled = form.name.trim() && form.email.trim() && form.clientBase && form.niche.trim();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    const payload = {
      email: form.email.trim(),
      name: form.name.trim(),
      website: normalizeWebsite(form.website),
      client_base: form.clientBase,
      niche: form.niche.trim(),
    };

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register-partner`, payload);
      const data = response.data;

      if (data.status === "error") {
        if (data.message === "The user already exists") {
          setError("This email is already registered. Log in to access your partner dashboard.");
        } else {
          setError(data.message || "Registration failed. Please try again.");
        }
        setIsSubmitting(false);
        return;
      }

      if (data.status !== "ok" || !data.token) {
        setError("Unexpected response from the server. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const normalizedEmail = payload.email.toLowerCase();
      setAuthSessionCookies({
        token: data.token,
        email: normalizedEmail,
        accountType: data.account_type || "partner",
        botType: null,
      });

      try {
        await axios.post(`${BACKEND_URL}/api/lead-inquiry`, {
          name: payload.name,
          email: normalizedEmail,
          industry: payload.niche || "Marketing Agency Partner",
          monthly_inbound: payload.client_base,
          primary_channel: payload.website || "fractolabs.com",
        });
      } catch (leadErr) {
        console.error("Partner lead inquiry error:", leadErr);
      }

      applyPostAuthRedirect(navigate, "/partner/dashboard");
    } catch (err) {
      console.error("Partner registration error:", err);
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Registration failed. Please try again.";
      setError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="partner-form"
      className="section-padding relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 80% 70% at 70% 0%, rgba(0,210,255,0.08) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 10% 80%, rgba(0,229,255,0.04) 0%, transparent 50%), #080A0F",
      }}
      data-testid="partner-form-section"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(135deg, transparent 40%, rgba(0,210,255,0.03) 40%, rgba(0,210,255,0.03) 40.5%, transparent 40.5%)",
        }}
        aria-hidden
      />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        <RevealOnScroll>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <span className="font-mono text-[10px] sm:text-xs text-partner-muted tracking-[0.2em]">
              {PARTNER_FORM_THESIS.tag}
            </span>
            <span className="font-mono text-[10px] text-partner-accent flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00D2FF] pulse-subtle" />
              {PARTNER_FORM_THESIS.status}
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold text-partner leading-tight mb-3 max-w-3xl">
            {PARTNER_FORM_THESIS.headline}{" "}
            <span className="gradient-text">{PARTNER_FORM_THESIS.headlineAccent}</span>
          </h2>
          <p className="text-partner-muted font-body max-w-2xl leading-relaxed mb-8">
            {PARTNER_FORM_THESIS.insight}
          </p>
        </RevealOnScroll>

        <RevealOnScroll delay={0.06}>
          <p className="text-sm text-partner-muted font-body mb-10" data-testid="partner-scan-strip">
            {PARTNER_SCAN.map((stat, i) => (
              <React.Fragment key={stat.label}>
                {i > 0 && " · "}
                <span className="text-partner-accent font-semibold font-mono">{stat.value}</span> {stat.label}
              </React.Fragment>
            ))}
          </p>
        </RevealOnScroll>

        <div className="grid lg:grid-cols-[1fr_1.15fr] gap-6 lg:gap-8 items-start">
          <RevealOnScroll delay={0.1}>
            <div
              className="rounded-2xl border border-partner bg-[#11151F] p-6 sm:p-8 h-full"
              data-testid="access-pipeline"
            >
              <div className="mb-6">
                <span className="font-mono text-[10px] text-partner-muted tracking-[0.15em]">
                  WHAT HAPPENS AFTER SUBMIT
                </span>
                <p className="text-sm text-partner font-semibold mt-2">
                  Three steps from form to first deployment
                </p>
              </div>

              {UNLOCK_STEPS.map((step, index) => (
                <UnlockStep
                  key={step.step}
                  step={step}
                  index={index}
                  isComplete={index === 0 && allRequiredFilled}
                  isActive={
                    index === 0 ? !allRequiredFilled : index === 1 && allRequiredFilled
                  }
                />
              ))}
            </div>
          </RevealOnScroll>

          <RevealOnScroll delay={0.14}>
            <div
              className="rounded-2xl border border-[#00D2FF]/30 bg-[#11151F] overflow-hidden"
              data-testid="credential-terminal"
            >
              <div className="px-5 sm:px-6 py-3 border-b border-partner">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-xs text-partner-muted">{progress}% complete</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#080A0F] border border-partner overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-[#00D2FF] to-[#00E5FF]"
                    initial={false}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    data-testid="form-progress-bar"
                  />
                </div>
              </div>

              <div className="p-5 sm:p-6">
                {error && (
                  <div className="mb-5 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm font-body">
                    {error}
                    {error.includes("already registered") && (
                      <>
                        {" "}
                        <Link to={appLoginPath("/partner/dashboard")} className="underline text-partner-accent hover:text-partner">
                          Log in
                        </Link>
                      </>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="partner-name" className="block font-mono text-[10px] text-partner-muted tracking-wider mb-1.5 uppercase">
                        Your Name
                      </label>
                      <input
                        id="partner-name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className={inputClass}
                        placeholder="Enter your full name"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="partner-email" className="block font-mono text-[10px] text-partner-muted tracking-wider mb-1.5 uppercase">
                        Company Work Email
                      </label>
                      <input
                        id="partner-email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className={inputClass}
                        placeholder="name@youragency.com"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="partner-website" className="block font-mono text-[10px] text-partner-muted tracking-wider mb-1.5 uppercase">
                        Agency Website
                      </label>
                      <input
                        id="partner-website"
                        name="website"
                        type="text"
                        inputMode="url"
                        autoComplete="url"
                        value={form.website}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="www.youragency.com"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="partner-client-base" className="block font-mono text-[10px] text-partner-muted tracking-wider mb-1.5 uppercase">
                        Active Client Base
                      </label>
                      <PartnerSelect
                        id="partner-client-base"
                        name="clientBase"
                        value={form.clientBase}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        placeholder="Select client count"
                        options={PARTNER_CLIENT_BASE_SELECT_OPTIONS}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="partner-niche" className="block font-mono text-[10px] text-partner-muted tracking-wider mb-1.5 uppercase">
                        Primary Niche / Industry
                      </label>
                      <input
                        id="partner-niche"
                        name="niche"
                        value={form.niche}
                        onChange={handleChange}
                        required
                        className={inputClass}
                        placeholder="e.g., Home Services, Real Estate, Medical"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <MagneticButton
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
                    data-testid="partner-form-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Issuing credentials…
                      </>
                    ) : (
                      <>
                        Login to Partner Dashboard
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </MagneticButton>

                  <p className="text-center font-mono text-[10px] text-partner-muted tracking-wider">
                    NO SALES CALL · INSTANT ACCESS · DEPLOY UNDER YOUR BRAND
                  </p>
                </form>
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
};

export default PartnerFormSection;
